import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router';
import type { ProjectListItem, ProjectStatus, UpdateProject } from '@mys/contracts';
import { ApiError } from '../lib/api.js';
import {
  daysLate,
  describeEvent,
  PROJECT_STATUSES,
  PROJECT_STATUS_LABELS,
  useProject,
  useProjectEvents,
  useUpdateProject,
} from '../lib/projects.js';
import { formatDate, formatDateTime, formatInr } from '../lib/format.js';

/* ============================================================================
   /projects/:id — one project, and the only screen that edits one.
   ----------------------------------------------------------------------------
   `launchedAt` is shown but never editable. It is set by the API the first
   time a project goes live, because "when did this go live" is a fact about
   what happened rather than a field to type — and a launch date that can be
   quietly retyped is a refund claim that can be quietly erased.
   ========================================================================== */

/** An ISO instant as the value an <input type="date"> wants. */
function toDateInput(iso: string | null): string {
  return iso ? (iso.slice(0, 10) ?? '') : '';
}

interface Fields {
  promisedDate: string;
  domain: string;
  previewUrl: string;
  liveUrl: string;
}

function fieldsOf(project: ProjectListItem): Fields {
  return {
    promisedDate: toDateInput(project.promisedDate),
    domain: project.domain ?? '',
    previewUrl: project.previewUrl ?? '',
    liveUrl: project.liveUrl ?? '',
  };
}

export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const { data, isPending, error } = useProject(id);
  const update = useUpdateProject(id);

  const [fields, setFields] = useState<Fields>({
    promisedDate: '',
    domain: '',
    previewUrl: '',
    liveUrl: '',
  });
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Once. A refetch must not overwrite what is half-typed.
    if (loaded || !data) return;
    setFields(fieldsOf(data.project));
    setLoaded(true);
  }, [data, loaded]);

  if (isPending) return <p className="muted">Loading…</p>;

  if (error) {
    return (
      <>
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'That project was not found.'}
        </p>
        <Link to="/projects" className="btn btn--ghost">
          Back to projects
        </Link>
      </>
    );
  }

  const project = data.project;
  const late = daysLate(project);

  function set<K extends keyof Fields>(key: K, value: string) {
    setFields((current) => ({ ...current, [key]: value }));
  }

  function onSave(event: FormEvent) {
    event.preventDefault();
    /* Empty means "clear this", which the API reads as null. That is why the
       contract uses clearable rather than optional fields: in a PATCH,
       `undefined` has to keep meaning "not sent". */
    const changes: UpdateProject = {
      promisedDate: fields.promisedDate === '' ? null : fields.promisedDate,
      domain: fields.domain.trim(),
      previewUrl: fields.previewUrl.trim(),
      liveUrl: fields.liveUrl.trim(),
    };
    update.mutate(changes);
  }

  const fieldError = (name: keyof Fields): string | undefined =>
    update.error instanceof ApiError ? update.error.fields[name] : undefined;

  const formError =
    update.error instanceof ApiError && Object.keys(update.error.fields).length === 0
      ? update.error.message
      : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{project.business}</h1>
          <p className="muted">
            {project.plan} · {formatInr(project.priceInr)} · banaya {formatDate(project.createdAt)}
          </p>
        </div>
        <div className="row">
          <Link to={`/invoices/new?projectId=${project.id}`} className="btn btn--ghost">
            Raise an invoice
          </Link>
          <Link to={`/clients/${project.clientId}`} className="btn btn--ghost">
            Open client
          </Link>
        </div>
      </div>

      {late !== null ? (
        <p className="alert" role="status">
          This project is {late} days late. Under /refund that is 10% off plus a free month of care,
          if the delay is ours.
        </p>
      ) : null}

      <div className="stack">
        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Stage</h2>
          <div className="row">
            {PROJECT_STATUSES.map((value) => (
              <button
                key={value}
                type="button"
                className={
                  project.status === value ? 'btn btn--small' : 'btn btn--ghost btn--small'
                }
                disabled={update.isPending || project.status === value}
                onClick={() => update.mutate({ status: value as ProjectStatus })}
              >
                {PROJECT_STATUS_LABELS[value]}
              </button>
            ))}
          </div>
          <p className="muted" style={{ margin: '10px 0 0', fontSize: 13 }}>
            {project.launchedAt
              ? `Went live ${formatDateTime(project.launchedAt)}.`
              : 'Moving it to live sets the launch date by itself.'}
          </p>
        </section>

        <section className="card">
          <h2 style={{ fontSize: 18, marginBottom: 12 }}>Details</h2>

          <form className="stack" onSubmit={onSave} noValidate>
            <div className="field">
              <label htmlFor="promisedDate">Promised date</label>
              <input
                id="promisedDate"
                type="date"
                value={fields.promisedDate}
                onChange={(event) => set('promisedDate', event.target.value)}
              />
              <span className="muted" style={{ fontSize: 13 }}>
                This is the date /refund is measured against. Think before you change it.
              </span>
              {fieldError('promisedDate') ? (
                <span className="field__error" role="alert">
                  {fieldError('promisedDate')}
                </span>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="domain">Domain</label>
              <input
                id="domain"
                value={fields.domain}
                maxLength={200}
                placeholder="sharmadental.in"
                onChange={(event) => set('domain', event.target.value)}
              />
              {fieldError('domain') ? (
                <span className="field__error" role="alert">
                  {fieldError('domain')}
                </span>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="previewUrl">Preview link</label>
              <input
                id="previewUrl"
                type="url"
                inputMode="url"
                value={fields.previewUrl}
                maxLength={500}
                placeholder="https://preview.example.in"
                onChange={(event) => set('previewUrl', event.target.value)}
              />
              {fieldError('previewUrl') ? (
                <span className="field__error" role="alert">
                  {fieldError('previewUrl')}
                </span>
              ) : null}
            </div>

            <div className="field">
              <label htmlFor="liveUrl">Live link</label>
              <input
                id="liveUrl"
                type="url"
                inputMode="url"
                value={fields.liveUrl}
                maxLength={500}
                placeholder="https://sharmadental.in"
                onChange={(event) => set('liveUrl', event.target.value)}
              />
              {fieldError('liveUrl') ? (
                <span className="field__error" role="alert">
                  {fieldError('liveUrl')}
                </span>
              ) : null}
            </div>

            {formError ? (
              <p className="alert" role="alert">
                {formError}
              </p>
            ) : null}

            <div className="row">
              <button className="btn" type="submit" disabled={update.isPending}>
                {update.isPending ? 'Saving…' : 'Save'}
              </button>
              {update.isSuccess && !update.isPending ? (
                <span className="muted" role="status">
                  Saved.
                </span>
              ) : null}
            </div>
          </form>
        </section>

        <div className="row">
          {project.previewUrl ? (
            <a
              className="btn btn--ghost btn--small"
              href={project.previewUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open preview
            </a>
          ) : null}
          {project.liveUrl ? (
            <a
              className="btn btn--ghost btn--small"
              href={project.liveUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open live site
            </a>
          ) : null}
        </div>

        <Link to="/projects" className="btn btn--ghost">
          Back to projects
        </Link>
      </div>

      <ProjectHistory id={id} />
    </>
  );
}

/* ============================================================================
   What has happened to this project.
   ----------------------------------------------------------------------------
   The promised date is what /refund is measured against — 10% off plus a free
   care month when a launch is late and it is our fault. Moving it is therefore
   not an ordinary edit, and until this existed the only record was a log line
   nobody reads and every log rotates away.

   Read-only, and deliberately so. A history anyone can edit answers no
   question worth asking it.
   ========================================================================== */

function ProjectHistory({ id }: { id: string }) {
  const { data, isPending, error } = useProjectEvents(id);
  const events = data?.events ?? [];

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <h2 style={{ marginTop: 0 }}>History</h2>

      {error ? (
        <p className="alert" role="alert">
          {error instanceof ApiError ? error.message : 'Could not load the history.'}
        </p>
      ) : null}

      {isPending ? <p className="muted">Loading…</p> : null}

      {!isPending && events.length === 0 ? (
        <p className="muted" style={{ margin: 0 }}>
          Nothing has changed yet. A new stage or promised date shows up here.
        </p>
      ) : null}

      <ol className="stack" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
        {events.map((event) => (
          <li key={event.id}>
            <strong>{describeEvent(event)}</strong>
            <p className="muted" style={{ fontSize: 13, margin: 0 }}>
              {formatDateTime(event.createdAt)}
              {/* Null when a job did it, or when the account has since been
                  deleted — both are honestly "nobody we can name". */}
              {event.authorName ? ` · ${event.authorName}` : ''}
            </p>
          </li>
        ))}
      </ol>
    </div>
  );
}
